import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { useLocation, useNavigate, useParams } from "react-router";
import { blockLabel, isInlineSuggestion } from "../document/blockUtils";
import { PanelEmptyState } from "./PanelEmptyState";
import { useApi } from "@/hooks/use-api";

export const SuggestionReferences = () => {
  const document = useAppState((state) => state.currentDocument);
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { documentId } = useParams();
  const { deleteSuggestion, updateBlock } = useApi();

  if (!document || !selectedGroup) return null;

  const findBlockGroup = (blockId: string) => {
    return document.groups.find((group) => group.blockIds.includes(blockId));
  };

  const handleDelete = async (
    suggestionId: string,
    primaryBlockId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    await deleteSuggestion(document.id, suggestionId);
    await updateBlock(document.id, primaryBlockId, {
      sentiment: null,
    });
  };

  const handleNavigateToBlock = (blockId: string) => {
    const blockGroup = findBlockGroup(blockId);
    if (!blockGroup) return;

    // Ensure we're in reader view
    if (documentId && !location.pathname.endsWith(`/documents/${documentId}`)) {
      navigate(`/documents/${documentId}`);
    }

    // Set the selected group
    dispatch({ type: "SET_SELECTED_GROUP", payload: blockGroup });

    // Set the selected block index
    const blockIndexInGroup = blockGroup.blockIds.indexOf(blockId);
    dispatch({ type: "SET_SELECTED_BLOCK_INDEX", payload: blockIndexInGroup });
  };

  // Include suggestions that
  // - Have at least one block in the current group
  // - Are approved
  // - Are comments
  // - Or inline suggestions with rubric criteria
  const suggestionsInGroup = document.suggestions
    .filter(
      (suggestion) =>
        suggestion.status === "approved" &&
        (!suggestion.rubricCriteriaId || isInlineSuggestion(suggestion)) &&
        suggestion.blocks.some((blockId) =>
          selectedGroup.blockIds.includes(blockId)
        )
    )
    .sort((a, b) => {
      // Sort by the position of the first block in the document
      const aFirstBlockIndex = document.blocks.findIndex(
        (block) => block.id === a.blocks[0]
      );
      const bFirstBlockIndex = document.blocks.findIndex(
        (block) => block.id === b.blocks[0]
      );
      return aFirstBlockIndex - bFirstBlockIndex;
    });

  const getSentimentColor = (sentiment: string) => {
    return sentiment === "positive"
      ? "border-positive"
      : sentiment === "negative"
      ? "border-negative"
      : "border-neutral";
  };

  const getSentimentBgColor = (sentiment: string) => {
    return sentiment === "positive"
      ? "bg-positive text-positive"
      : sentiment === "negative"
      ? "bg-negative text-negative"
      : "bg-neutral text-neutral";
  };

  const getBlockSentiment = (blockId: string) => {
    const block = document.blocks.find((block) => block.id === blockId);
    return block?.sentiment || "neutral";
  };

  if (suggestionsInGroup.length === 0) {
    return (
      <PanelEmptyState
        title="No notes"
        message="Your comments and remarks will appear here."
      />
    );
  }

  return (
    <>
      {suggestionsInGroup.map((suggestion) => {
        const primaryBlockId =
          suggestion.primaryBlockId ?? suggestion.blocks[0];
        const primaryBlockSentiment = getBlockSentiment(primaryBlockId);
        if (!suggestion.id) return null;
        return (
          <div key={suggestion.id}>
            <div className="flex justify-between items-center mb-2">
              <Badge className={getSentimentBgColor(primaryBlockSentiment)}>
                {blockLabel(document, primaryBlockId)}
              </Badge>
              <button
                onClick={(e) =>
                  handleDelete(
                    suggestion.id!,
                    suggestion.primaryBlockId ?? suggestion.blocks[0],
                    e
                  )
                }
                className="text-zinc-500 hover:text-zinc-600 transition-colors text-xs font-semibold cursor-pointer"
              >
                Delete
              </button>
            </div>
            <Card
              className={cn(
                getSentimentColor(primaryBlockSentiment),
                "border border-dashed py-1 flex-1 mb-4 shadow-none rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
              )}
              onClick={() => handleNavigateToBlock(primaryBlockId)}
            >
              <CardContent className="px-2">
                <div className="text-sm py-1">{suggestion.text}</div>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </>
  );
};
